import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserChip from './UserChip';
import Wordmark from './Wordmark';

const GOV_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><path d="M2 4h10M2 7h6M2 10h4" /></svg>;
const MGMT_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><rect x="1" y="2" width="12" height="2" rx="1" /><rect x="1" y="6" width="8" height="2" rx="1" /><rect x="1" y="10" width="10" height="2" rx="1" /></svg>;
const WB_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><rect x="2" y="1" width="10" height="12" rx="1.5" /><path d="M4 5h6M4 7.5h4" /></svg>;
const FIN_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><path d="M2 10l3-3 2 2 5-5" /></svg>;
const INIT_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><path d="M2 3h10M2 7h6M2 11h8" /><circle cx="11" cy="11" r="2" fill="currentColor" stroke="none" opacity="0.35" /></svg>;
const REPORT_ICON = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"><rect x="2" y="1.5" width="10" height="11" rx="1.5" /><path d="M4.5 5h5M4.5 7.5h5M4.5 10h3" /></svg>;

// Initiation sequence (brief §1540) — programme/project context from outlet.
const INITIATION_PILLAR = {
  id: 'init', label: 'Initiation', color: '#534AB7', icon: INIT_ICON,
  items: [
    { label: 'Charter', seg: 'initiation/charter' },
    { label: 'Process scope', seg: 'initiation/process-scope' },
    { label: 'Requirements', seg: 'initiation/requirements' },
    { label: 'Solution options', seg: 'initiation/solution-options' },
    { label: 'Bake-off comparison', seg: 'initiation/bake-off' },
    { label: 'ROM estimate', seg: 'initiation/rom' },
  ],
};

// Pillars for the PROJECT workspace (/project/:id/...). Project-scoped delivery.
const PROJECT_PILLARS = [
  INITIATION_PILLAR,
  {
    id: 'gov', label: 'Governance', color: '#534AB7', icon: GOV_ICON,
    items: [
      { label: 'Stage gates', seg: 'governance/stage-gates' },
      { label: 'RAID log', seg: 'governance/raid' },
      { label: 'Decisions', seg: 'governance/decisions' },
    ],
  },
  {
    id: 'mgmt', label: 'Management', color: '#1D9E75', icon: MGMT_ICON,
    items: [
      { label: 'Plan & milestones', seg: 'management/plan' },
      { label: 'Role proposal', seg: 'management/roles' },
      { label: 'Resource allocation', seg: 'management/resource' },
    ],
  },
  {
    id: 'wb', label: 'Workbench', color: '#EF9F27', icon: WB_ICON,
    items: [
      { label: 'Process design', seg: 'workbench/design/process-design' },
      { label: 'Functional specifications', seg: 'workbench/design/functional-specs' },
      { label: 'Disposition', seg: 'workbench/build/disposition' },
      { label: 'Build item tracker', seg: 'workbench/build/tracker' },
      { label: 'Test scripts', seg: 'workbench/test/scripts' },
      { label: 'Defects', seg: 'workbench/test/defects' },
      { label: 'Test management', seg: 'workbench/test' },
    ],
  },
  {
    id: 'fin', label: 'Finance & reporting', color: '#185FA5', icon: FIN_ICON,
    items: [
      { label: 'Budget & forecast', seg: 'finance/budget' },
      { label: 'Benefits', seg: 'finance/benefits' },
    ],
  },
];

// Pillars for the PROGRAMME rollup (/programme/:id/...). Cross-project reporting + programme initiation.
const PROGRAMME_PILLARS = [
  {
    id: 'init', label: 'Initiation', color: '#534AB7', icon: INIT_ICON,
    items: [
      { label: 'Programme charter', seg: 'initiation/charter' },
    ],
  },
  {
    id: 'report', label: 'Reporting', color: '#1D9E75', icon: REPORT_ICON,
    items: [
      { label: 'Status reports', seg: 'management/status-reports' },
    ],
  },
  {
    id: 'settings', label: 'Settings', color: '#185FA5', icon: GOV_ICON,
    items: [
      { label: 'Programme members', seg: 'settings/members', roles: ['admin'] },
    ],
  },
];

function canSee(item, role) {
  if (!item.roles) return true;
  return item.roles.includes(role);
}

/**
 * Generic Layer 2 sidebar. Drives navigation from `basePath` (e.g. /project/:id
 * or /programme/:id) and a `pillars` definition. Used by both the project
 * workspace and the programme rollup.
 */
export default function WorkspaceSidebar({ basePath, pillars = PROJECT_PILLARS, role = 'admin', subLabel = 'Workspace', backLabel = 'All programmes', backTo = '/home' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const relative = location.pathname.startsWith(basePath) ? location.pathname.slice(basePath.length).replace(/^\//, '') : '';

  const [collapsed, setCollapsed] = useState(false);
  const brandMark = <Wordmark className="logo-v3" />;
  const activePillar = pillars.find((p) => p.items.some((it) => relative.startsWith(it.seg)));
  const [openPillar, setOpenPillar] = useState(activePillar?.id || null);

  const isOverview = relative === '';

  function go(seg) {
    navigate(`${basePath}/${seg}`);
  }

  if (collapsed) {
    return (
      <div className="sidebar" style={{ width: 48, transition: 'width 0.2s ease' }}>
        <div style={{ padding: '12px 0', borderBottom: '0.5px solid #d3d1c7', display: 'flex', justifyContent: 'center' }}>
          <button type="button" className="toggle-btn" style={{ width: 28, height: 28 }} onClick={() => setCollapsed(false)}>›</button>
        </div>
        <div style={{ flex: 1, padding: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, overflowY: 'auto' }}>
          <button type="button" title="Overview" onClick={() => navigate(basePath)}
            style={{ width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer', background: isOverview ? '#EEEDFE' : 'transparent', color: isOverview ? '#534AB7' : '#888780', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="1" y="1" width="5" height="5" rx="1" /><rect x="8" y="1" width="5" height="5" rx="1" /><rect x="1" y="8" width="5" height="5" rx="1" /><rect x="8" y="8" width="5" height="5" rx="1" /></svg>
          </button>
          {pillars.map((p) => (
            <button key={p.id} type="button" title={p.label} onClick={() => { setCollapsed(false); setOpenPillar(p.id); }}
              style={{ width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {p.icon}
            </button>
          ))}
        </div>
        <UserChip collapsed />
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sb-head">
        <div>
          {brandMark}
          <div className="logo-sub">{subLabel}</div>
        </div>
        <button type="button" className="toggle-btn" onClick={() => setCollapsed(true)} title="Collapse sidebar">‹</button>
      </div>

      <div className="sb-back">
        <button type="button" className="sb-back-btn" onClick={() => navigate(backTo)}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M9 11L5 7l4-4" /></svg>
          {backLabel}
        </button>
      </div>

      <div className="nav-full" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div className={`ni ${isOverview ? 'active' : ''}`} onClick={() => navigate(basePath)} style={{ cursor: 'pointer', marginBottom: 4 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="1" y="1" width="5" height="5" rx="1" /><rect x="8" y="1" width="5" height="5" rx="1" /><rect x="1" y="8" width="5" height="5" rx="1" /><rect x="8" y="8" width="5" height="5" rx="1" /></svg>
          Overview
        </div>

        {pillars.map((pillar) => {
          const items = pillar.items.filter((it) => canSee(it, role));
          if (!items.length) return null;
          const open = openPillar === pillar.id;
          return (
            <div className="pillar-item" key={pillar.id}>
              <div
                className={`pillar-hdr ${open ? 'open' : ''}`}
                onClick={() => setOpenPillar(open ? null : pillar.id)}
              >
                <span className="pillar-ic" style={{ color: pillar.color }}>{pillar.icon}</span>
                <span className="pillar-lbl">{pillar.label}</span>
                <span className="pillar-arr">▶</span>
              </div>
              <div className={`pillar-sub ${open ? 'open' : ''}`}>
                {items.map((it) => (
                  <div
                    key={it.seg}
                    className={`ni ${relative.startsWith(it.seg) ? 'active' : ''}`}
                    onClick={() => go(it.seg)}
                    style={{ cursor: 'pointer' }}
                  >
                    {it.label}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <UserChip />
    </div>
  );
}

export { PROJECT_PILLARS, PROGRAMME_PILLARS, INITIATION_PILLAR };
